import {ActionItemEventWireDto, ActionItemWireDto} from '../shell-communication/action-board-operations';
import {actionSubjectLabel, completionTag, formatDayKey, formatInstant, isActive, isCompleted, overdueDays, parsePayload, severityLabel, sourceChip} from './action-board.model';

/**
 * CR-20260922-001 — the strings a card and the reading pane show for one item, computed once from
 * the item, the trading day and the user's time zone. Shared by both so they can never disagree.
 */
export interface ActionItemView {
    subjectLabel: string;
    /** The header line: the expiring leg's DISPLAY NAME when there is one, the count when several
     * (owner 2026-09-22); the item's own headline for every other type. */
    headline: string;
    /** One display name per expiring leg for the pane's bullet list; empty otherwise. */
    legLines: string[];
    /** The portfolio the item belongs to, shown as the second chip after the source (owner
     * 2026-09-23); empty for a Global item. */
    portfolioChip: string;
    /** The source chip: ALERTS for a notice (owner 2026-09-23), the producer's label otherwise. */
    sourceChip: string;
    /** A notice's sender, shown in the pane; empty for produced items. */
    sender: string;
    /** CRITICAL / WARNING / REGULAR. */
    severityLabel: string;
    isLive: boolean;
    isDone: boolean;
    overdue: number;
    completionTag: string;
    originated: string;
    due: string;
    delivered: string;
    read: string;
    verified: string;
    openPanelLabel: string;
    canOpen: boolean;
    canAcknowledge: boolean;
    openExplanation: string;
    availabilityLabel?: string;
}

/** Turns an option ticker into its display name; the platform's display-name service supplies it. */
export type TickerDisplayName = (ticker: string) => string;

export function describeItem(item: ActionItemWireDto, todayDayKey: string, zone: string | null,
                             displayName?: TickerDisplayName): ActionItemView {
    const live = isActive(item);
    const {headline, legLines} = presentLegs(item, displayName);
    const done = isCompleted(item);
    const tag = completionTag(item);
    const openPanelLabel = item.itemType === 'AvailableAdjustment' ? 'ORION'
        : item.openPanel === 'HedgingGrid' ? 'Hedging Grid'
        : item.openPanel === 'Collars' ? 'Portfolio Hedging'
        : item.openPanel;

    let verified: string;
    if (item.completedAtUtc) {
        verified = `${tag.toLowerCase()} ${formatInstant(item.completedAtUtc, zone)}`;
    } else if (item.itemType === 'AvailableAdjustment') {
        const proposal = parsePayload(item);
        verified = proposal.health === 'unconfirmed' ? 'waiting for confirmed data'
            : proposal.availability === 'available' ? 'available' : 'unavailable';
    } else if (item.itemType === 'Notice') {
        verified = 'awaiting acknowledge';
    } else if (item.itemType === 'ExpiringToday') {
        verified = item.lastEvaluatedAtUtc
            ? `not yet (open the panel, or last run ${formatInstant(item.lastEvaluatedAtUtc, zone)})`
            : 'not yet (open the panel)';
    } else {
        verified = item.lastEvaluatedAtUtc
            ? `not yet (last run ${formatInstant(item.lastEvaluatedAtUtc, zone)})`
            : 'not yet';
    }

    // Opening the panel IS the acknowledgement of an outstanding ExpiringToday card (owner 2026-09-23).
    const marksDone = item.itemType === 'ExpiringToday' && live ? '; marks this item done' : '';
    let openExplanation: string;
    if (item.itemType === 'AvailableAdjustment') {
        openExplanation = 'opens the saved hedge in ORION Adjust; Preview is your choice';
    } else if (item.itemType === 'ExpiringToday' && item.openPanel === 'HedgingGrid') {
        openExplanation = 'opens the Hedging Grid with the portfolio loaded and today\'s expiry selected' + marksDone;
    } else if (item.itemType === 'ExpiringToday') {
        openExplanation = 'opens APG with the portfolio loaded' + marksDone;
    } else if (item.itemType === 'ZoneDrift') {
        openExplanation = 'opens APG with the portfolio loaded; the zone is the one APG reads, never pre-set';
    } else if (item.itemType === 'AnalysisOverdue') {
        // CR-20260930-001 M4 (owner): the overdue card opens COLLARS - the acquisition happens there.
        openExplanation = 'opens Portfolio Hedging with the combo selected, ready to acquire or clear';
    } else {
        openExplanation = `opens ${openPanelLabel} with the portfolio loaded`;
    }

    return {
        subjectLabel: actionSubjectLabel(item),
        headline,
        legLines,
        portfolioChip: portfolioOf(item),
        sourceChip: item.itemType === 'AvailableAdjustment' ? 'ORION' : sourceChip(item),
        sender: item.itemType === 'Notice' ? item.sourceLabel : '',
        severityLabel: item.severity === 'green' ? '' : severityLabel(item.severity),
        isLive: live,
        isDone: done,
        overdue: overdueDays(item, todayDayKey),
        completionTag: tag,
        originated: formatInstant(item.originatedAtUtc, zone),
        due: formatDayKey(item.dueDayKey),
        delivered: item.deliveredAtUtc ? formatInstant(item.deliveredAtUtc, zone) : 'not yet',
        read: item.readAtUtc ? formatInstant(item.readAtUtc, zone) : 'not yet',
        verified,
        openPanelLabel,
        canOpen: !!item.openPanel,
        canAcknowledge: item.cardAction === 'acknowledge' && live,
        openExplanation,
        availabilityLabel: item.itemType === 'AvailableAdjustment' && !done
            && parsePayload(item).health === 'confirmed'
            && parsePayload(item).availability !== 'available'
            && parsePayload(item).reason === 'outside-band' ? 'MISSED' : undefined
    };
}

/** "APG/Ports-3", "APG/Ports-3/Hedging", "HedgingGrid/Book-1" → the portfolio segment; "Global" → "". */
export function portfolioOf(item: ActionItemWireDto): string {
    const parts = (item.subjectLabel || '').split('/');
    return parts.length >= 2 ? parts[1] : '';
}

function presentLegs(item: ActionItemWireDto, displayName?: TickerDisplayName): { headline: string, legLines: string[] } {
    if (item.itemType === 'AvailableAdjustment') {
        const payload = parsePayload(item);
        const hedgeLabel = payload && typeof payload.hedgeLabel === 'string' ? payload.hedgeLabel.trim() : '';
        return {headline: hedgeLabel ? `${hedgeLabel} — ${item.headline}` : item.headline, legLines: []};
    }
    // ZoneDrift shows the market zone and the short zone only — no legs (owner 2026-09-23).
    if (item.itemType !== 'ExpiringToday') {
        return {headline: item.headline, legLines: []};
    }
    const legs = (parsePayload(item).legs || []).filter(l => !!l && !!l.ticker);
    if (legs.length === 0) {
        return {headline: item.headline, legLines: []};
    }
    const name = (t: string) => (displayName ? displayName(t) : t);
    // Display names only — no quantity, no side (owner 2026-09-22).
    const lines = legs.map(l => name(l.ticker));

    if (legs.length === 1) {
        return {headline: `${name(legs[0].ticker)} expires today`, legLines: lines};
    }

    const where = item.subjectPath.endsWith('/Hedging') ? ' (hedges)' : '';
    const portfolio = (item.subjectLabel || '').split('/')[1] || item.subjectLabel;
    return {headline: `${legs.length} legs in ${portfolio}${where} expire today`, legLines: lines};
}

export function describeEvent(e: ActionItemEventWireDto, zone: string | null): string {
    if ((e.kind === 'completed' && e.note === 'withdrawn') || e.kind === 'withdrawn') {
        let explanation: string;
        if (e.actor?.startsWith('sender:')) {
            explanation = 'The sender withdrew this alert.';
        } else {
            let reason: string;
            try { reason = JSON.parse(e.evidence || '{}')?.reason; } catch (_) { /* Historical evidence may be absent or malformed. */ }
            explanation = reason === 'source-removed' ? 'Not Applicable: the original hedge was removed.'
                : reason === 'source-changed' ? "Not Applicable: the original hedge's positions changed."
                    : reason ? 'Not Applicable: the saved reason could not be interpreted.'
                        : 'Not Applicable: the reason was not recorded.';
        }
        return `${formatInstant(e.atUtc, zone)} · ${explanation}`;
    }
    const note = e.note ? ` (${e.note})` : '';
    return `${formatInstant(e.atUtc, zone)} · ${e.kind} · ${e.actor}${note}`;
}

/** "Bob Proctor" → "Bob P." (the platform's convention for subordinate names); "Bob P." stays. */
export function shortUserName(name: string): string {
    if (!name) {
        return name;
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) {
        return name;
    }
    const last = parts[parts.length - 1];
    if (last.length <= 2 && last.endsWith('.')) {
        return name;
    }
    return `${parts[0]} ${last.charAt(0)}.`;
}
